<template>
  <list-page-shell>
    <template #actions>
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openSubForm(null)">{{ $t('common.create') }}</el-button>
    </template>

    <template #filters>
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item :label="$t('subcontractorListPage.keywordLabel')">
          <el-input
            v-model="filters.keyword"
            clearable
            :placeholder="$t('subcontractorListPage.keywordPh')"
            style="width:240px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item :label="$t('subcontractorListPage.partyTypeFilter')">
          <el-select v-model="filters.partyTypeCode" clearable :placeholder="$t('subcontractorListPage.partyTypeAll')" style="width:160px;">
            <el-option :label="$t('enum.subcontractorParty.1')" :value="1" />
            <el-option :label="$t('enum.subcontractorParty.2')" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" :disabled="loading" @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

    <el-table :data="list" border stripe v-loading="loading" style="width:100%;">
      <el-table-column prop="companyName" :label="$t('grid.company')" min-width="120" />
      <el-table-column prop="subcontractorName" :label="$t('grid.dba')" />
      <el-table-column :label="$t('subcontractorListPage.partyTypeCol')" width="120" show-overflow-tooltip>
        <template slot-scope="s">{{ labelPartyType(s.row.partyTypeCode) }}</template>
      </el-table-column>
      <el-table-column prop="contactPerson" :label="$t('grid.personName')" />
      <el-table-column prop="licenseNo" :label="$t('grid.licenseNo')" width="100" />
      <el-table-column prop="phone" :label="$t('grid.phone')" />
      <el-table-column prop="email" :label="$t('grid.email')" />
      <el-table-column :label="$t('common.actions')" width="140">
        <template slot-scope="s">
          <el-button type="text" size="small" @click="openSubForm(s.row.id)">{{ $t('common.edit') }}</el-button>
          <el-button type="text" size="small" v-permission="['OFFICE','ADMIN']" @click="remove(s.row)">{{ $t('common.delete') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="list-pager">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :page-sizes="[10, 20, 50]"
        :total="total"
        :page-size="pageSize"
        :current-page="pageNo"
        @size-change="onSizeChange"
        @current-change="p => { pageNo = p; load() }"
      />
    </div>

    <RecordFormDialog :visible.sync="subFormVisible" :title="subFormTitle">
      <SubcontractorForm
        v-if="subFormVisible"
        ref="subFormRef"
        :key="subFormKey"
        embedded
        :record-id="subFormRecordId"
        @saved="onSubFormSaved"
        @saving="v => (subFormSaving = v)"
      />
      <template slot="footer">
        <el-button @click="subFormVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="subFormSaving" @click="submitSubFormDialog">{{ $t('common.save') }}</el-button>
      </template>
    </RecordFormDialog>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import RecordFormDialog from '@/components/RecordFormDialog.vue'
import SubcontractorForm from '@/views/subcontractor/SubcontractorForm.vue'
import { getSubcontractorPage, deleteSubcontractor } from '@/api/subcontractor'

export default {
  name: 'SubcontractorList',
  components: { ListPageShell, RecordFormDialog, SubcontractorForm },
  data() {
    return {
      subFormVisible: false,
      subFormSaving: false,
      subFormRecordId: null,
      subFormKey: 0,
      list: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      loading: false,
      filters: { keyword: '', partyTypeCode: null }
    }
  },
  computed: {
    subFormTitle() {
      return this.subFormRecordId
        ? this.$t('subcontractorFormPage.editTitle')
        : this.$t('subcontractorFormPage.newTitle')
    }
  },
  created() {
    this.load()
  },
  methods: {
    openSubForm(id) {
      this.subFormRecordId = id != null ? id : null
      this.subFormKey += 1
      this.subFormVisible = true
    },
    onSubFormSaved() {
      this.subFormVisible = false
      this.load()
    },
    submitSubFormDialog() {
      if (this.$refs.subFormRef) this.$refs.subFormRef.submit()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.load()
    },
    handleSearch() {
      this.pageNo = 1
      this.load()
    },
    handleReset() {
      this.filters = { keyword: '', partyTypeCode: null }
      this.pageNo = 1
      this.load()
    },
    labelPartyType(code) {
      const c = code == null ? 3 : Number(code)
      const key = 'enum.subcontractorParty.' + c
      const t = this.$t(key)
      return t === key ? this.$t('enum.subcontractorParty.3') : t
    },
    async load() {
      this.loading = true
      try {
        const params = { pageNo: this.pageNo, pageSize: this.pageSize }
        const kw = (this.filters.keyword || '').trim()
        if (kw) params.keyword = kw
        const pt = this.filters.partyTypeCode
        if (pt != null && pt !== '') params.partyTypeCode = pt
        const r = await getSubcontractorPage(params)
        const d = r.data || r
        this.list = d.records || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    },
    async remove(row) {
      await this.$confirm(this.$t('subcontractorListPage.confirmDelete'), this.$t('common.tip'), { type: 'warning' })
      await deleteSubcontractor(row.id)
      this.$message.success(this.$t('common.deleted'))
      this.load()
    }
  }
}
</script>

<style scoped>
.list-pager {
  margin-top: 16px;
  text-align: right;
}
</style>
